// The invoice document. Printed A4 portrait — that is what a client files and
// what the accountant staples, so the on-screen document is held to the same
// width and the print rules simply strip the chrome around it.

import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { money, date } from '../lib/fmt';
import { Button, Card, Note, PageHead, Pill, Tag, Tile, Tiles,
         Table, Th, Td, TableWrap } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton, Bone, Lines } from '../components/skeleton';

interface Detail {
  _id: string; number: string; doc_type: 'invoice' | 'credit_note';
  issue_date: string; due_date?: string | null;
  period_from?: string | null; period_to?: string | null; po_number?: string | null;
  currency: string; fx_rate: number; place_of_supply?: string | null; tax_treatment: string;
  subtotal: number; igst: number; cgst: number; sgst: number; total: number;
  status: string; notes?: string | null;
  client: { name: string; legal_name?: string; gstin?: string; billing_address?: string;
            tds_rate_bp?: number; tds_section?: string };
  contact?: { name: string; email?: string } | null;
  ref_invoice?: { number: string } | null;
  lines: { item: string; description?: string; sac_code: string; amount: number; is_passthrough: boolean }[];
  payments: { _id: string; paid_on: string; amount: number; tds_amount: number;
              bank_charges: number; method?: string; reference?: string }[];
  settlement: { total: number; received: number; tds: number; credited: number; outstanding: number };
}

const TREATMENT: Record<string, string> = {
  igst: 'IGST 18%', cgst_sgst: 'CGST 9% + SGST 9%',
  export: 'Export — zero rated', exempt: 'No GST',
};

/**
 * Head, the settlement figures, the document itself, then payments received.
 *
 * The document keeps its page proportions while it loads — it is the thing a
 * person came to look at, and it is the width of an A4 sheet, not the column.
 */
function InvoiceSkeleton() {
  return (
    <Loading label="Loading the invoice">
      <HeadSkeleton actions={2} />
      <TilesSkeleton n={4} hero={false} />
      <div className="bg-card border border-rule rounded shadow-sm p-9 max-w-[210mm] mx-auto">
        <Bone w={190} h={19} className="mx-auto" />
        <Bone w={140} h={11} className="mx-auto mt-2.5 mb-7" />
        <div className="grid grid-cols-2 gap-8 mb-7">
          {[0, 1].map((i) => (
            <div key={i}>
              <Bone w={72} h={9} className="mb-2.5" />
              <Lines n={4} />
            </div>
          ))}
        </div>
        <TableSkeleton rows={4} cols={5} cards={false} />
        <div className="flex justify-end mt-6">
          <div className="w-[280px] flex flex-col gap-2.5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex justify-between gap-6">
                <Bone w={92} h={11} />
                <Bone w={64} h={11} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <CardSkeleton flush className="mt-5" sub={false}>
        <TableSkeleton rows={2} cols={4} />
      </CardSkeleton>
    </Loading>
  );
}

export default function InvoiceDetail() {
  const { id } = useParams();
  const { meta } = useSession();
  const { data, loading, error } = useAsync<Detail>(() => api.get(`/api/invoices/${id}`), [id]);

  if (loading) return <InvoiceSkeleton />;
  if (error) return <Note title="That did not load" tone="critical">{error}</Note>;
  const inv = data!;
  const org = meta?.org ?? {};
  const isCN = inv.doc_type === 'credit_note';
  const m = (p: number) => money(p, inv.currency, 'auto');
  const md = (p: number) => money(p, inv.currency, true);   // the document keeps its paise
  const out = inv.settlement.outstanding;

  return (
    <div>
      <PageHead
        eyebrow={<><Link to="/invoices" className="text-inherit no-underline">← Invoices</Link>
                 {`  ·  ${isCN ? 'Credit note' : 'Tax invoice'}`}</>}
        title={inv.number}
        dek={`${inv.client.name} · ${date(inv.issue_date)}${
          inv.period_from ? ` · service ${date(inv.period_from)} – ${date(inv.period_to)}` : ''}`}
        actions={<>
          <Pill kind={isCN ? 'credit_note' : inv.status}>{isCN ? 'Credit note' : inv.status.replace('_', ' ')}</Pill>
          <Button onClick={() => window.print()}>Print</Button>
        </>} />

      {!isCN && (
        <Tiles>
          <Tile label="Invoice total" value={m(inv.total)}
                sub={<span className="text-muted">{TREATMENT[inv.tax_treatment]} · taxable {m(inv.subtotal)}</span>} />
          <Tile label="Received" value={m(inv.settlement.received)}
                sub={<span className="text-muted">
                  {inv.settlement.tds ? `plus ${m(inv.settlement.tds)} TDS withheld` : 'in the bank'}</span>} />
          <Tile label="Outstanding" value={m(out)}
                sub={<span className="text-muted">{out > 0 && inv.due_date
                  ? (inv.due_date < new Date().toISOString().slice(0, 10)
                      ? `overdue since ${date(inv.due_date)}` : `due ${date(inv.due_date)}`)
                  : 'settled in full'}</span>} />
          <Tile label="Expected TDS"
                value={m(Math.round(inv.subtotal * (inv.client.tds_rate_bp ?? 0) / 10000))}
                sub={<span className="text-muted">
                  {inv.client.tds_section ? `u/s ${inv.client.tds_section}` : 'no section on file'}</span>} />
        </Tiles>
      )}

      {inv.ref_invoice && (
        <Note title="References">
          {isCN ? 'Issued against invoice ' : 'Credited by '}<b>{inv.ref_invoice.number}</b>.
        </Note>
      )}

      <div className="print-doc bg-card border border-rule rounded shadow-sm p-9 max-w-[210mm] mx-auto">
        <h2 className="font-display text-[21px] font-bold tracking-[0.02em] text-center m-0 mb-1">
          {isCN ? 'CREDIT NOTE' : 'TAX INVOICE'}
        </h2>
        <p className="text-center text-[13px] text-ink-2 m-0 mb-7">{org.legal_name}</p>

        <div className="grid grid-cols-2 gap-8 mb-7">
          <div>
            <h5 className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted m-0 mb-2">Supplier</h5>
            <p className="m-0 text-[12.5px] leading-relaxed text-ink-2">
              <b className="text-ink">{org.legal_name}</b><br />{org.address}<br />
              {org.city} {org.pincode}<br />GSTIN {org.gstin}<br />PAN {org.pan}
            </p>
          </div>
          <div>
            <h5 className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted m-0 mb-2">Bill to</h5>
            <p className="m-0 text-[12.5px] leading-relaxed text-ink-2">
              <b className="text-ink">{inv.client.legal_name ?? inv.client.name}</b><br />
              {inv.client.billing_address && <>{inv.client.billing_address}<br /></>}
              {inv.client.gstin ? `GSTIN ${inv.client.gstin}` : 'Unregistered / overseas'}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-1 text-[12.5px] mb-6">
          {([
            [isCN ? 'CN No.' : 'Invoice No.', inv.number],
            ['Date', date(inv.issue_date)],
            inv.period_from && ['Service period', `${date(inv.period_from)} – ${date(inv.period_to)}`],
            inv.po_number && ['P.O.', inv.po_number],
            inv.contact && ['Requestor', `${inv.contact.name}${inv.contact.email ? ` (${inv.contact.email})` : ''}`],
            ['Place of supply', inv.place_of_supply
              ? `${meta?.states[inv.place_of_supply] ?? inv.place_of_supply} (${inv.place_of_supply})` : '—'],
            inv.currency !== 'INR' && ['Exchange rate', `1 ${inv.currency} = ₹${inv.fx_rate}`],
          ].filter(Boolean) as [string, string][]).map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="font-mono text-[11px] text-muted">{k}</dt>
              <dd className="m-0 font-medium text-ink">{v}</dd>
            </div>
          ))}
        </dl>

        <TableWrap>
          <Table>
            <thead><tr><Th>Item</Th><Th>Description</Th><Th>HSN/SAC</Th><Th n>Amount</Th></tr></thead>
            <tbody>
              {inv.lines.map((l, i) => (
                <tr key={i}>
                  <Td>{l.item}{l.is_passthrough && <> <Tag>pass-through</Tag></>}</Td>
                  <Td className="text-ink-2">{l.description ?? ''}</Td>
                  <Td className="font-mono text-[11.5px]">{l.sac_code}</Td>
                  <Td n>{md(l.amount)}</Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <TotalRow label="Taxable value" value={md(inv.subtotal)} />
              {inv.igst > 0 && <TotalRow label="IGST @ 18%" value={md(inv.igst)} />}
              {inv.cgst > 0 && <TotalRow label="CGST @ 9%" value={md(inv.cgst)} />}
              {inv.sgst > 0 && <TotalRow label="SGST @ 9%" value={md(inv.sgst)} />}
              {inv.tax_treatment === 'export' && <TotalRow label="GST" value="Zero-rated export under LUT" />}
              <TotalRow label={isCN ? 'Total credited' : 'Total'} value={md(inv.total)} strong />
            </tfoot>
          </Table>
        </TableWrap>

        {inv.notes && <p className="mt-4 text-[12.5px] text-ink-2">{inv.notes}</p>}

        <div className="flex flex-wrap justify-between gap-6 mt-7 pt-4 border-t border-rule text-xs text-ink-2">
          <div>
            <b>Bank details</b><br />{org.bank_name}, {org.bank_branch}<br />
            A/C {org.bank_account} · IFSC {org.bank_ifsc}
            {inv.currency !== 'INR' && <><br />SWIFT {org.swift}</>}
          </div>
          <div className="text-right">
            For <b>{org.legal_name}</b><br /><br />
            <span className="text-muted">{org.signatory}</span><br />
            <span className="text-muted text-[11px]">Authorised signatory</span>
          </div>
        </div>
      </div>

      {inv.payments.length > 0 && (
        <Card title="Payments received" className="mt-5 no-print" flush>
          <TableWrap>
            <Table>
              <thead><tr><Th>Date</Th><Th>Method</Th><Th>Reference</Th>
                <Th n>Banked</Th><Th n>TDS</Th><Th n>Charges</Th></tr></thead>
              <tbody>
                {inv.payments.map((p) => (
                  <tr key={p._id}>
                    <Td className="font-mono text-[11.5px]">{date(p.paid_on)}</Td>
                    <Td>{p.method ?? '—'}</Td>
                    <Td className="font-mono text-[11.5px]">{p.reference ?? '—'}</Td>
                    <Td n>{m(p.amount)}</Td>
                    <Td n>{p.tds_amount ? m(p.tds_amount) : '—'}</Td>
                    <Td n>{p.bank_charges ? m(p.bank_charges) : '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}

const TotalRow = ({ label, value, strong }: { label: string; value: string; strong?: boolean }) => (
  <tr className={strong ? 'border-t border-rule' : ''}>
    <td colSpan={3} className={`px-3 py-2 text-right ${strong ? 'text-ink font-semibold' : 'text-ink-2'}`}>{label}</td>
    <td className={`px-3 py-2 text-right tnum ${strong ? 'font-semibold' : ''}`}>{value}</td>
  </tr>
);
